import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag } from 'lucide-react';
import { BRANCHES } from '../config/siteConfig';
import { WhatsAppMessageService } from '../utils/whatsappMessageService';
import { useCartStore } from '../hooks/useCartStore';

const WhatsAppFAB: React.FC = () => {
    const [isNearFooter, setIsNearFooter] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredBranch, setHoveredBranch] = useState<string | null>(null);
    const { items, setIsDrawerOpen, isDrawerOpen } = useCartStore();
    const totalItems = items.length;

    // Architect: Close branch selector if the inquiry drawer opens to avoid UI conflict
    useEffect(() => {
        if (isDrawerOpen) {
            setIsOpen(false);
            setHoveredBranch(null);
        }
    }, [isDrawerOpen]);

    useEffect(() => {
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY;
            const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

            // Move button up when within 200px of footer
            setIsNearFooter(distanceFromBottom < 200);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial position

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleBranchPhoneClick = (branchId: string, phoneIndex: number) => {
        const branch = BRANCHES.find(b => b.id === branchId);
        if (!branch) return;

        const phone = branch.phones[phoneIndex];
        const message = WhatsAppMessageService.buildGeneralMessage();

        const success = WhatsAppMessageService.openWhatsApp(phone, message);

        if (success) {
            WhatsAppMessageService.trackWhatsAppClick({
                source: 'floating_button',
                branch: branch.name,
                phoneIndex,
                phoneNumber: phone,
                messageType: 'general'
            });

            setIsOpen(false);
            setHoveredBranch(null);
        }
    };

    const handleFABClick = () => {
        if (totalItems > 0) {
            setIsDrawerOpen(true);
            return;
        }

        // If only 1 branch and 1 phone, open directly
        if (BRANCHES.length === 1 && BRANCHES[0].phones.length === 1) {
            handleBranchPhoneClick(BRANCHES[0].id, 0);
        } else {
            setIsOpen(!isOpen);
        }
    };

    return (
        <>
            {/* Main FAB Button */}
            <motion.button
                onClick={handleFABClick}
                className="fixed right-6 z-50 group transition-all duration-300"
                style={{
                    bottom: isNearFooter ? '120px' : '24px'
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: 0.5
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Contactar por WhatsApp"
            >
                {/* Pulse Animation Ring */}
                {!isOpen && totalItems === 0 && (
                    <motion.div
                        className="absolute inset-0 rounded-full bg-[#25D366] opacity-75"
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.75, 0, 0.75],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                )}

                {/* Main Button */}
                <div className={`relative w-12 h-12 md:w-14 md:h-14 ${totalItems > 0 ? 'bg-zinc-900 border-2 border-white' : 'bg-[#25D366]'} rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(37,211,102,0.4)]`}>
                    {isOpen ? (
                        <X className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    ) : totalItems > 0 ? (
                        <ShoppingBag className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    ) : (
                        <svg
                            viewBox="0 0 24 24"
                            className="w-6 h-6 md:w-8 md:h-8 fill-white"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                    )}

                    {/* Badge */}
                    {totalItems > 0 && !isOpen && (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 w-6 h-6 bg-white text-black rounded-full flex items-center justify-center text-[10px] font-black border-2 border-black"
                        >
                            {totalItems}
                        </motion.div>
                    )}
                </div>

                {/* Tooltip on Hover (Desktop) - Only show when closed and empty */}
                {!isOpen && totalItems === 0 && (
                    <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none hidden md:block">
                        <div className="bg-zinc-900 text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap border border-zinc-700">
                            <p className="font-display text-sm font-bold">¿Necesitas ayuda?</p>
                            <p className="text-xs text-gray-400">Chatea con nosotros</p>
                        </div>
                        {/* Arrow */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-zinc-900"></div>
                    </div>
                )}
            </motion.button>

            {/* Branch Selector Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ duration: 0.2 }}
                        className="fixed right-6 z-40"
                        style={{
                            bottom: isNearFooter ? '190px' : '94px'
                        }}
                    >
                        <div className="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl w-72 overflow-hidden">
                            <div className="p-4 border-b border-zinc-800">
                                <h3 className="text-white font-display font-bold text-sm uppercase">Contactar por WhatsApp</h3>
                                <p className="text-zinc-500 text-xs mt-1">Elige un asesor para ayudarte</p>
                            </div>
                            <div className="p-2 max-h-80 overflow-y-auto">
                                {BRANCHES.map((branch) => (
                                    <div
                                        key={branch.id}
                                        className="mb-1"
                                    >
                                        <div className="px-1 py-1 rounded">
                                            {BRANCHES.length > 1 && (
                                                <div className="flex items-center justify-between mb-2 px-2">
                                                    <span className="text-white font-medium text-xs flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 bg-[#25D366] rounded-full"></span>
                                                        {branch.name}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="space-y-1">
                                                {branch.phones.map((phone, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => handleBranchPhoneClick(branch.id, index)}
                                                        className="w-full text-left px-3 py-2.5 rounded text-sm font-mono font-bold text-zinc-300 hover:text-white hover:bg-[#25D366] transition-all flex items-center justify-between group/item"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <svg className="w-4 h-4 fill-current text-[#25D366] group-hover/item:text-white" viewBox="0 0 24 24">
                                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                            </svg>
                                                            {phone}
                                                        </div>
                                                        <span className="text-[10px] uppercase font-black opacity-40 group-hover/item:opacity-100">Chat</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default WhatsAppFAB;
